import { useState, useCallback } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api, DfeDefinitionSummary, DfeDefinitionListResult } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface UseItemDefinitionsOptions {
  search?: string;
  fungibility?: string;
  pageSize?: number;
}

interface UseItemDefinitionsReturn {
  definitions: DfeDefinitionSummary[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
  page: number;
  totalPages: number;
  goToPage: (page: number) => void;
}

export function useItemDefinitions(
  options: UseItemDefinitionsOptions = {},
): UseItemDefinitionsReturn {
  const limit = options.pageSize || 20;
  const [page, setPage] = useState(1);

  const search = options.search || undefined;
  const fungibility = options.fungibility || undefined;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.dfe.definitionList({ search, fungibility, page }),
    queryFn: (): Promise<DfeDefinitionListResult> =>
      api.listDfeDefinitions({
        search,
        fungibility,
        limit,
        offset: (page - 1) * limit,
      }),
    placeholderData: keepPreviousData,
  });

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const offset = (page - 1) * limit;

  const goToPage = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const loadMore = useCallback(() => {
    if (!isLoading && offset + limit < total) {
      setPage((prev) => prev + 1);
    }
  }, [isLoading, offset, limit, total]);

  const refresh = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    definitions: data?.definitions ?? [],
    total,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load definitions' : null,
    refresh,
    loadMore,
    hasMore: offset + limit < total,
    page,
    totalPages,
    goToPage,
  };
}
